import React, { Children, forwardRef, useEffect, useMemo, useState } from 'react';
import { useTestId } from '../../hooks/useTestId/useTestId';
import { BrandColour } from '../../fundamentals/colours/variants';
import { PX_TO_REM, SIZES } from '../../fundamentals/sizes/sizes';
import { ITabPanelProps } from './TabPanel';
import { DEFAULT_BUTTON_SIZE, DEFAULT_BUTTON_VARIANT } from '../Button/Button';
import { IGutterProps } from '../../fundamentals/spaces/space';
import { TabsContext } from './TabsContext';
import { SpaceContainer } from '../../fundamentals/spaces/SpaceContainer';

export type TabsSize = typeof SIZES.X_SMALL | typeof SIZES.SMALL | typeof SIZES.MEDIUM;

export interface ITabsProps extends IGutterProps {
  variant?: BrandColour;
  size?: TabsSize;
  // add underline in each tab when value is true and
  // a border on the tablist when value is 'bordered'
  underline?: boolean | 'bordered';
  defaultActive?: number;
  onAddTabClick?: () => void;
}

export const UNDERLINE_HIGHLIGHT_SIZE = PX_TO_REM[2];

export const Tabs = forwardRef<HTMLDivElement, React.PropsWithChildren<ITabsProps>>(
  ({ children, size = DEFAULT_BUTTON_SIZE, defaultActive = 1, underline, variant = DEFAULT_BUTTON_VARIANT, onAddTabClick, ...rest }, ref) => {
    const { generateTestId } = useTestId(rest, 'Tabs');
    const [active, setActive] = useState(defaultActive);
    const context = useMemo(() => ({ active, setActive, variant, size, underline, onAddTabClick }), [active, variant, size, underline, onAddTabClick]);

    useEffect(() => {
      setActive(defaultActive);
    }, [defaultActive]);

    return (
      <SpaceContainer ref={ref} {...generateTestId()} {...rest}>
        <TabsContext.Provider value={context}>
          {Children.map(children, (tabPanel, position) => {
            const props: ITabPanelProps = position === 0 ? {} : { show: active === position };
            return React.cloneElement(tabPanel as any, props);
          })}
        </TabsContext.Provider>
      </SpaceContainer>
    );
  }
);
